import { roundCents } from '../core/math';
import { INDEX, PRICES, TIPS } from './config';
import { EGG_LABEL, TOPPING_INFO, isAllTheWay, orderedKinds } from './lingo';
import type {
  Doneness,
  EggStyle,
  GameResult,
  HashBrownSpec,
  IndexLevel,
  OrderSpec,
  PlatedItem,
  Ticket,
  Topping,
  WaffleKind,
} from './types';

export interface Evaluation {
  quality: number;
  issues: string[];
}

const DONENESS_SCORE: Record<'regular' | 'well', Record<Exclude<Doneness, 'burnt'>, number>> = {
  regular: { raw: 0.35, golden: 1, crispy: 0.75 },
  well: { raw: 0.25, golden: 0.65, crispy: 1 },
};

export function evaluateHashBrowns(
  spec: HashBrownSpec,
  toppings: readonly Topping[],
  doneness: Doneness,
): Evaluation {
  const issues: string[] = [];
  if (doneness === 'burnt') return { quality: 0, issues: ['these are charcoal'] };

  const wanted = new Set(spec.toppings);
  const got = new Set(toppings);
  const missing = spec.toppings.filter((t) => !got.has(t));
  const extra = toppings.filter((t) => !wanted.has(t));
  for (const t of missing) issues.push(`no ${TOPPING_INFO[t].ingredient}?`);
  for (const t of extra) issues.push(`I didn't ask for ${TOPPING_INFO[t].ingredient}`);

  // All the way has eight toppings, so each miss hurts a little less.
  const step = isAllTheWay(spec.toppings) ? 0.12 : 0.22;
  const toppingScore = Math.max(0, 1 - step * (missing.length + extra.length));

  const donenessScore = DONENESS_SCORE[spec.well ? 'well' : 'regular'][doneness];
  if (doneness === 'raw') issues.push('these are RAW');
  else if (spec.well && doneness === 'golden') issues.push('I said WELL');
  else if (!spec.well && doneness === 'crispy') issues.push('a lil crispy');

  return { quality: roundQuality(toppingScore * donenessScore), issues };
}

export function evaluateWaffle(ordered: WaffleKind, poured: WaffleKind): Evaluation {
  if (ordered === poured) return { quality: 1, issues: [] };
  return {
    quality: 0.45,
    issues: [ordered === 'pecan' ? 'where are my pecans' : 'why are there nuts in this'],
  };
}

export function evaluateEggs(ordered: EggStyle, cooked: EggStyle): Evaluation {
  if (ordered === cooked) return { quality: 1, issues: [] };
  return { quality: 0.5, issues: [`I said ${EGG_LABEL[ordered].toLowerCase()}`] };
}

function roundQuality(q: number): number {
  return Math.round(q * 1000) / 1000;
}

export function isTicketComplete(ticket: Ticket): boolean {
  return orderedKinds(ticket.order).every((k) => ticket.plated[k] !== undefined);
}

export function ticketQuality(ticket: Ticket): number {
  const items = orderedKinds(ticket.order)
    .map((k) => ticket.plated[k])
    .filter((p): p is PlatedItem => p !== undefined);
  if (items.length === 0) return 0;
  return roundQuality(items.reduce((sum, p) => sum + p.quality, 0) / items.length);
}

export function isPerfect(ticket: Ticket): boolean {
  const kinds = orderedKinds(ticket.order);
  return kinds.length > 0 && kinds.every((k) => ticket.plated[k]?.quality === 1);
}

export function ticketIssues(ticket: Ticket): string[] {
  return orderedKinds(ticket.order).flatMap((k) => ticket.plated[k]?.issues ?? []);
}

export function orderSubtotal(order: OrderSpec, eggSurcharge: boolean): number {
  let total = 0;
  if (order.hashBrowns) {
    total += isAllTheWay(order.hashBrowns.toppings)
      ? PRICES.allTheWay
      : PRICES.hashBrowns + PRICES.topping * order.hashBrowns.toppings.length;
  }
  if (order.waffle) total += PRICES.waffle[order.waffle];
  if (order.eggs) total += PRICES.eggs + (eggSurcharge ? PRICES.eggSurcharge : 0);
  if (order.bacon) total += PRICES.bacon;
  return roundCents(total);
}

export interface TipInput {
  subtotal: number;
  quality: number;
  /** 0 … 1 patience left when served. */
  patience: number;
  tipMult: number;
  /** Product of active multipliers (unbothered, live stream). */
  multiplier: number;
  /** Current perfect-order streak (after this order). */
  combo: number;
}

export function computeTip(input: TipInput): number {
  const rate = TIPS.baseRate + TIPS.qualityRate * input.quality + TIPS.speedRate * input.patience;
  const comboBonus =
    input.quality === 1 ? TIPS.comboStep * Math.min(input.combo, TIPS.comboCap) : 0;
  const tip = (input.subtotal * rate * input.tipMult + comboBonus) * input.multiplier;
  return roundCents(Math.max(0.25, tip));
}

/** Index change for a served ticket. */
export function indexDeltaForService(quality: number): number {
  if (quality >= 1) return INDEX.perfectOrder;
  if (quality >= 0.8) return INDEX.goodOrder;
  if (quality >= 0.5) return INDEX.okOrder;
  return INDEX.badOrder;
}

export function indexLevel(value: number): IndexLevel {
  if (value <= 0) return 'red';
  if (value < INDEX.greenAt) return 'yellow';
  return 'green';
}

export const LEVEL_EMOJI: Record<IndexLevel, string> = {
  green: '🟩',
  yellow: '🟨',
  red: '🟥',
};

export interface Rank {
  title: string;
  blurb: string;
}

/** Tip thresholds for each rank, calibrated against simulated competent play. */
export const RANK_THRESHOLDS = [
  { min: 0, title: 'TRAINEE', blurb: 'Cried in the walk-in. Twice. Still showed up.' },
  { min: 100, title: 'GRILL OPERATOR', blurb: 'The hash browns were, in fact, scattered.' },
  { min: 180, title: 'SCATTERED, SMOTHERED & CERTIFIED', blurb: 'You speak fluent hash brown.' },
  {
    min: 270,
    title: 'UNBOTHERED LEGEND',
    blurb: 'A chair flew past your head. You did not look up.',
  },
  {
    min: 380,
    title: 'WAFFLE HOUSE FINAL BOSS',
    blurb: 'The Avengers call YOU when things get bad.',
  },
] as const;

export function rankFor(result: Pick<GameResult, 'reason' | 'tips' | 'hostPleased'>): Rank {
  if (result.reason === 'closed') {
    return {
      title: 'FEMA HAS BEEN NOTIFIED',
      blurb: 'The Waffle House Index hit red. Somewhere, a meteorologist gasped.',
    };
  }
  if (result.hostPleased) {
    return {
      title: 'THE NEW HOST',
      blurb: 'The Waffle House has found its new host. It does not explain. It does not need to.',
    };
  }
  let rank: Rank = RANK_THRESHOLDS[0];
  for (const r of RANK_THRESHOLDS) if (result.tips >= r.min) rank = r;
  return { title: rank.title, blurb: rank.blurb };
}
